import moment from 'moment';
import { PagoRequest, PagoRequestPagoDoctoRel } from '../../../models/Pago';
import { FiscalpopCFDI, ToBillArguments, ToBillEvent } from '../../../models/Factura';
import { ProfileQL } from '../../../models/Profile';

/** A pending bill argument with its stamped CFDI and how much is left to pay */
export interface ArgumentBinded {
    argument: ToBillArguments<ToBillEvent>;
    cfdi: FiscalpopCFDI;
    insoluto: number;
}

export const SET_EMPTY_PAGO_FORM = (currentUser: ProfileQL) => ({
    lugarExpedicion: currentUser.fiscalpopProfile.lugarExpedicion,
    serie: 'PAGO',
    folio: '1',
    receptor: { nombre: '', rfc: '', usoCFDI: 'P01', regimen: '', zip: '' },
    pagos: [{
        fecha: moment(Date.now()).format('YYYY-MM-DD'),
        formaDePago: '',
        monto: 0,
        // ------------
        facturaRelacionada: []
    }],
} as PagoRequest)

type PagoProperty = 'receptor' | 'pago' | 'facturaRelacionada' | 'comprobante' | '_replace';

export function pagoReducer(state: PagoRequest, { property, value }: { property: PagoProperty, value: any }): PagoRequest {
    if (property === 'receptor') {
        return Object.assign({}, state, { receptor: value });
    } else if (property === 'pago') {
        const pago = Object.assign({}, state.pagos[0], value);
        return Object.assign({}, state, { pagos: [pago] });
    }
    else if (property === 'comprobante') {
        return Object.assign({}, state, value);
    }
    else if (property === 'facturaRelacionada') {
        const _pago = state.pagos[0];
        _pago.facturaRelacionada = value;
        return Object.assign({}, state, { pagos: [_pago] });
    }
    else if (property === '_replace') {
        return Object.assign({}, state, value);
    }
    else {
        // Force update only
        return Object.assign({}, state);
    }
}

const roundCents = (value: number) => Math.round(value * 100) / 100;

/**
 * Builds the "documento relacionado" list out of the selected bills, spreading any
 * leftover cents of the declared monto into the bills that can still absorb them.
 */
export function buildFacturasRelacionadas(binders: ArgumentBinded[], previousRelDocs: PagoRequestPagoDoctoRel[], monto: number) {
    const relDocs: PagoRequestPagoDoctoRel[] = binders.map((binder) => {
        const offset = binder.argument.paymentEvents.some(p => p.paymentNum === 0);
        const nextPayEvent = binder.argument.paymentEvents.filter(ev => !ev.isClosed || (!!ev.isClosed && !!ev.wasCanceled))[0];
        let importePagago = nextPayEvent.paymentCFDI.pagos[0].monto;
        if (importePagago > binder.insoluto) {
            importePagago = binder.insoluto;
        }
        return ({
            uuid: binder.cfdi.uuid,
            importePagado: importePagago,
            numParcialidad: nextPayEvent.paymentNum + (offset ? 1 : 0)
        })
    })

    // Adjust possitive cents
    const montoTotal = previousRelDocs.reduce((p, c) => roundCents(p + c.importePagado), 0);
    if ((monto - montoTotal) > 0) {
        let toAdjust = roundCents(monto - montoTotal);
        for (const rel of relDocs) {
            const binderIs = binders.find(b => b.cfdi.uuid === rel.uuid);
            const difference = roundCents(binderIs.insoluto - rel.importePagado);
            if (difference <= toAdjust) {
                rel.importePagado = binderIs.insoluto;
                toAdjust = roundCents(toAdjust - difference);
            }
        }
    }

    return {
        facturaRelacionada: relDocs,
        monto: relDocs.reduce((p, c) => roundCents(p + c.importePagado), 0)
    }
}
